import React, { useMemo } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { Load, LoadCase, SnappingUnderlay } from '@paragon/contract-library';
import { DataRow, LoadGroup } from '@paragon/layout-models';

import { SelectableItemEntry } from '../../../../../library/selection/types';
import { useAppMonitor } from '../../../../../monitor';
import { cycleVisibilityOfItem } from '../../../../../store/actions/visualOptionActions';
import { getActiveUnderlayVisibility } from '../../../../../store/selectors/snappingGuideLineSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { RowActionButton } from './common/RowActionButton';
import { RowButtonProps } from './common/RowButtonProps';

const HIDEABLE_ITEM_TYPES = ['loadCase', 'loadGroup', 'load', 'snappingUnderlay'];

export function ToggleVisibilityButton({ row }: RowButtonProps) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const visibleLoadGuids = useAppSelector((state) => state.visualOptions.visibleLoadGuids);
  const isUnderlayVisible = useAppSelector(getActiveUnderlayVisibility);

  const icon = useMemo(() => {
    switch (row.type) {
      case 'load':
        return visibleLoadGuids[(row as DataRow<Load>).id] ? VisibilityIcon : VisibilityOffIcon;

      case 'loadCase':
      case 'loadGroup':
        return (row as DataRow<LoadGroup | LoadCase>).item.loads.every((load) => !visibleLoadGuids[load.guid])
          ? VisibilityOffIcon
          : VisibilityIcon;

      case 'snappingUnderlay':
        return isUnderlayVisible((row as DataRow<SnappingUnderlay>).id) ? VisibilityIcon : VisibilityOffIcon;

      default:
        return null;
    }
  }, [row, visibleLoadGuids, isUnderlayVisible]);

  if (!HIDEABLE_ITEM_TYPES.includes(row.type)) {
    return null;
  }

  return (
    <RowActionButton
      icon={icon!}
      name="toggleVisibility"
      title="Toggle Visibility Modes"
      onClick={() => {
        const { item, type } = row as DataRow<any>;
        const entry = { item, type } as SelectableItemEntry;

        dispatch(cycleVisibilityOfItem(entry));
        monitor.recordInteraction('Toggle Visibility button clicked');
      }}
    />
  );
}
